import { useQuery } from '@tanstack/react-query';
import { subDays } from 'date-fns';
import { last } from 'lodash';

import { api } from '@api';
import { CLIMATE_TYPES } from '@models/temperature.types';
import { useApartment } from '@stores/apartmentStore';
import { authStore } from '@stores/authStore';

export const useRoomTemperature = (apartmentId?: string) => {
  const { apartment } = useApartment(apartmentId);

  const { data, isLoading } = useQuery(
    ['/climate/:id', apartment?.apartmentId],
    async () => {
      try {
        if (!apartment?.apartmentId) throw Error('apartment ID required');

        // Timestamp from 7 days ago
        const startTimeStamp = subDays(new Date(), 7).getTime() / 1000;

        const response = await api.measurements.getClimate({
          apartmentId: apartment.apartmentId,
          startDateTime: Math.floor(startTimeStamp),
          measurementType: CLIMATE_TYPES.TEMPERATURE,
        });

        return response.data[0]?.measurements ?? [];
      } catch (err) {
        return [];
      }
    },
    /* check authStore.isAuthenticated state on re-renders but don't trigger
     * useQuery on authStore.isAuthenticated change (since it would require wrapping
     * the hook inside an observer somehow). This is because useQuery hooks are only
     * invoked when the user is accessing an authenticated route, i.e. has authenticated
     * already. However, when logging-out, authStore.isAuthenticated state changes before
     * unmounting the authenticated routes and, therefore, up-to-date authentications
     * state must be checked to avoid illegal API calls on log-out */
    { enabled: !!apartment?.apartmentId && authStore.isAuthenticated }
  );

  return {
    measurements: data ?? [],
    mostRecent: last(data),
    isLoading,
  };
};
